import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef } from "react";
import { Switch, Popconfirm, Input, Checkbox, Row, Col, Select, Form, Space, Radio, Tooltip } from "antd";
import { FormSettingsGeneralNavTable, LearnMoreTag, UpsellTag } from "../..";
import { LABEL_GTM, LABEL_MTM } from "@devowl-wp/cookie-consent-web-client";
import { useFormSettings, useI18n, useUpsell } from "../../../contexts";
import { useNavLinksPageTypes } from "../../../hooks";
import { deepClone } from "@devowl-wp/react-utils";
const useCreateCookiePolicyPageButton = ()=>{
    const { createCookiePolicy } = useFormSettings();
    const availablePageTypes = useNavLinksPageTypes();
    const { getFieldValue, setFieldsValue } = Form.useFormInstance();
    return async ()=>{
        const { pageId, onNewNavLinks } = await createCookiePolicy();
        const bannerLinksClone = deepClone(getFieldValue("navLinks"));
        // Remove existing cookie policy link in footer links
        const cookiePolicyIdx = bannerLinksClone.findIndex((param)=>{
            let { pageType } = param;
            return pageType === "cookiePolicy";
        });
        if (cookiePolicyIdx > -1) {
            bannerLinksClone.splice(cookiePolicyIdx, 1);
        }
        // Add new cookie policy link in footer links
        const privacyPolicyIdx = bannerLinksClone.findIndex((param)=>{
            let { pageType } = param;
            return pageType === "privacyPolicy";
        });
        bannerLinksClone.splice(privacyPolicyIdx + 1, 0, {
            isExternalUrl: false,
            isTargetBlank: true,
            hideCookieBanner: true,
            pageId,
            pageType: "cookiePolicy",
            label: availablePageTypes.find((param)=>{
                let { name } = param;
                return name === "cookiePolicy";
            }).label
        });
        setFieldsValue({
            cookiePolicyId: pageId,
            navLinks: bannerLinksClone
        });
        await (onNewNavLinks == null ? void 0 : onNewNavLinks(bannerLinksClone));
    };
};
const FormSettingsGeneralCreateCookiePolicyPageButton = ()=>{
    const { __ } = useI18n();
    const handleCreateCookiePolicy = useCreateCookiePolicyPageButton();
    return /*#__PURE__*/ _jsx(Popconfirm, {
        title: __("I am aware that the texts in the cookie policy are suggested formulations and that the manufacturer of Real Cookie Banner cannot take any liability for the topicality, correctness or completeness of the information. I will check the information on my own responsibility, added missing information and correct information that does not fit to my specific use case on my own responsibility."),
        cancelText: __("Cancel"),
        okText: __("Create page"),
        overlayStyle: {
            maxWidth: 450
        },
        onConfirm: handleCreateCookiePolicy,
        placement: "bottomLeft",
        children: /*#__PURE__*/ _jsx("button", {
            className: "button",
            type: "button",
            style: {
                margin: "2px 0"
            },
            children: __("Create page")
        })
    });
};
const FormSettingsGeneralTabContent = ()=>{
    const { __, _x, _i } = useI18n();
    const { isPro } = useUpsell();
    const { iso3166OneAlpha2, renderPageSelector: RenderPageSelector, deactivateCookiePolicy, onCustomizeClick } = useFormSettings();
    const cookiePolicyUrl = useRef();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Form.Item, {
                noStyle: true,
                shouldUpdate: (prevValues, nextValues)=>prevValues.isBannerActive !== nextValues.isBannerActive,
                children: (param)=>{
                    let { getFieldValue, setFieldsValue } = param;
                    const isBannerActive = getFieldValue("isBannerActive");
                    return /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __("Cookie Banner/Dialog"),
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        noStyle: true,
                                        name: "isBannerActive",
                                        valuePropName: "checked",
                                        children: /*#__PURE__*/ _jsx(Switch, {
                                            style: {
                                                display: isBannerActive ? undefined : "none"
                                            }
                                        })
                                    }),
                                    !isBannerActive && /*#__PURE__*/ _jsx(Popconfirm, {
                                        title: _x("I am aware that the texts in the cookie banner and content blocker are suggested formulations and that the manufacturer of Real Cookie Banner cannot take any liability for the topicality, correctness or completeness of the information. I have checked the information on my own responsibility, added missing information and correct information that does not fit to my specific use case on my own responsibility.", "legal-text"),
                                        cancelText: __("Cancel"),
                                        okText: __("Activate now!"),
                                        overlayStyle: {
                                            maxWidth: 450
                                        },
                                        onCancel: ()=>{
                                            setFieldsValue({
                                                isBannerActive: false
                                            });
                                        },
                                        onConfirm: ()=>{
                                            setFieldsValue({
                                                isBannerActive: true
                                            });
                                        },
                                        placement: "bottomLeft",
                                        children: /*#__PURE__*/ _jsx(Switch, {
                                            checked: false
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        style: {
                                            marginTop: 5
                                        },
                                        children: __("You can enable and disable the cookie banner. We recommend to activate the cookie banner on your website after you have added all services.")
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __("Content Blocker"),
                                style: {
                                    display: isBannerActive ? undefined : "none"
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "isBlockerActive",
                                        valuePropName: "checked",
                                        noStyle: true,
                                        children: /*#__PURE__*/ _jsx(Switch, {
                                            style: {
                                                marginTop: 5
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        style: {
                                            marginTop: 5
                                        },
                                        children: __("This feature allows you to block content that would process personal data and set cookies, but for which you do not yet have the visitor's consent.")
                                    })
                                ]
                            })
                        ]
                    });
                }
            }),
            /*#__PURE__*/ _jsxs(Form.Item, {
                label: __("Website operator details"),
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "description",
                        style: {
                            marginTop: 7,
                            marginBottom: 12
                        },
                        children: __("You as a website operator should provide your address and at least one digital contact option to fulfill your information obligations for self-hosted services. If you have a company with subsidiaries, please disclose the company that is responsible for the data processing on their website.")
                    }),
                    /*#__PURE__*/ _jsxs(Row, {
                        gutter: [
                            10,
                            10
                        ],
                        children: [
                            /*#__PURE__*/ _jsx(Col, {
                                span: 24,
                                children: /*#__PURE__*/ _jsx(Form.Item, {
                                    name: "operatorContactAddress",
                                    style: {
                                        margin: 0
                                    },
                                    children: /*#__PURE__*/ _jsx(Input, {
                                        addonBefore: __("Full address"),
                                        placeholder: __("e.g. Global Co., 90210 Broadway Blvd., Nashville, TN 37011-5678 (without country)")
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Col, {
                                span: 12,
                                children: /*#__PURE__*/ _jsxs(Space.Compact, {
                                    className: "rcb-antd-select-addon",
                                    children: [
                                        /*#__PURE__*/ _jsx(Input, {
                                            addonBefore: __("Country")
                                        }),
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            name: "operatorCountry",
                                            noStyle: true,
                                            children: /*#__PURE__*/ _jsx(Select, {
                                                showSearch: true,
                                                optionFilterProp: "children",
                                                children: Object.keys(iso3166OneAlpha2).map((code)=>/*#__PURE__*/ _jsx(Select.Option, {
                                                        value: code,
                                                        children: iso3166OneAlpha2[code]
                                                    }, code))
                                            })
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(Col, {
                                span: 12,
                                children: /*#__PURE__*/ _jsx(Form.Item, {
                                    name: "operatorContactPhone",
                                    style: {
                                        margin: 0
                                    },
                                    children: /*#__PURE__*/ _jsx(Input, {
                                        addonBefore: __("Phone")
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Col, {
                                span: 12,
                                children: /*#__PURE__*/ _jsx(Form.Item, {
                                    name: "operatorContactEmail",
                                    rules: [
                                        {
                                            type: "email",
                                            message: __("Please provide a valid email!")
                                        }
                                    ],
                                    style: {
                                        margin: 0
                                    },
                                    children: /*#__PURE__*/ _jsx(Input, {
                                        addonBefore: __("Email")
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Col, {
                                span: 12,
                                children: /*#__PURE__*/ _jsxs(Space.Compact, {
                                    className: "rcb-antd-select-addon",
                                    children: [
                                        /*#__PURE__*/ _jsx(Input, {
                                            addonBefore: __("Contact form")
                                        }),
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            name: "operatorContactFormId",
                                            noStyle: true,
                                            children: /*#__PURE__*/ _jsx(RenderPageSelector, {
                                                id: "operatorContactFormId",
                                                disabled: false,
                                                multiple: false,
                                                onlyPages: true
                                            })
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Form.Item, {
                label: __("Legal basis to be applied"),
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "territorialLegalBasis",
                        noStyle: true,
                        rules: [
                            {
                                required: true,
                                message: __("Please choose at least one legal basis to apply!")
                            }
                        ],
                        children: /*#__PURE__*/ _jsxs(Checkbox.Group, {
                            style: {
                                marginTop: 5
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Checkbox, {
                                    value: "gdpr-eprivacy",
                                    children: __("GDPR / ePrivacy Directive")
                                }),
                                /*#__PURE__*/ _jsx(Checkbox, {
                                    value: "dsg-switzerland",
                                    children: __("DSG (Switzerland)")
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "description",
                        style: {
                            marginBottom: 0
                        },
                        children: __("Your website, and consequently the cookie banner, can be aligned to different legal bases. If you select multiple legal bases, suggestions in the settings will always match the stricter legal basis.")
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                label: __("Cookie policy page"),
                shouldUpdate: (param, param1)=>{
                    let { cookiePolicyId: a } = param;
                    let { cookiePolicyId: b } = param1;
                    return a !== b;
                },
                children: (param)=>{
                    let { getFieldValue, setFieldsValue } = param;
                    const cookiePolicyId = getFieldValue("cookiePolicyId");
                    return /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                style: {
                                    display: cookiePolicyId > 0 ? "block" : "none",
                                    maxWidth: 400
                                },
                                children: /*#__PURE__*/ _jsxs(Space.Compact, {
                                    className: "rcb-antd-select-addon",
                                    children: [
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            name: "cookiePolicyId",
                                            noStyle: true,
                                            children: /*#__PURE__*/ _jsx(RenderPageSelector, {
                                                id: "cookiePolicyId",
                                                disabled: true,
                                                multiple: false,
                                                onlyPages: true,
                                                selectedPageUrl: (url)=>{
                                                    cookiePolicyUrl.current = url;
                                                }
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Input, {
                                            addonAfter: /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsx("a", {
                                                        onClick: ()=>onCustomizeClick("rcb-cookie-policy-instruction"),
                                                        children: __("Customize")
                                                    }),
                                                    "\xa0•\xa0",
                                                    /*#__PURE__*/ _jsx("a", {
                                                        onClick: ()=>window.open(cookiePolicyUrl.current, "_blank"),
                                                        children: __("Open page")
                                                    }),
                                                    "\xa0•\xa0",
                                                    /*#__PURE__*/ _jsx(Popconfirm, {
                                                        title: __("If you want to deactivate the cookie policy page, the page created for it will also be deleted."),
                                                        cancelText: __("Cancel"),
                                                        okText: __("Deactivate and delete page"),
                                                        onConfirm: async ()=>{
                                                            const { onNewNavLinks } = await deactivateCookiePolicy(cookiePolicyId);
                                                            const newNavLinks = getFieldValue("navLinks").filter((param)=>{
                                                                let { pageType } = param;
                                                                return pageType !== "cookiePolicy";
                                                            });
                                                            setFieldsValue({
                                                                cookiePolicyId: 0,
                                                                navLinks: newNavLinks
                                                            });
                                                            await (onNewNavLinks == null ? void 0 : onNewNavLinks(newNavLinks));
                                                        },
                                                        overlayStyle: {
                                                            maxWidth: 450
                                                        },
                                                        placement: "bottomLeft",
                                                        children: /*#__PURE__*/ _jsx("a", {
                                                            children: __("Deactivate")
                                                        })
                                                    })
                                                ]
                                            })
                                        })
                                    ]
                                })
                            }),
                            !cookiePolicyId && /*#__PURE__*/ _jsx(FormSettingsGeneralCreateCookiePolicyPageButton, {}),
                            /*#__PURE__*/ _jsx("div", {
                                style: {
                                    margin: "5px 0px 0px"
                                },
                                children: /*#__PURE__*/ _jsx(LearnMoreTag, {
                                    url: __("https://devowl.io/knowledge-base/cookie-policy-wordpress-website/")
                                })
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "description",
                                style: {
                                    marginBottom: 0
                                },
                                children: _i(__("A cookie policy is a document that lists all the cookies and similar instruments used on a website and provides comprehensive information about each cookie. There is {{strong}}no obligation{{/strong}} to create a cookie policy under the GDPR or the ePrivacy Directive. But it can help you to present the handling of cookies and cookie-like technologies to your website visitors transparently and separately from your privacy policy."), {
                                    strong: /*#__PURE__*/ _jsx("strong", {})
                                })
                            })
                        ]
                    });
                }
            }),
            /*#__PURE__*/ _jsxs(Form.Item, {
                label: __("Footer links"),
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "description",
                        style: {
                            marginTop: 7,
                            marginBottom: 12
                        },
                        children: __("According to the ePrivacy Directive, legally required pages such as the privacy policy may be accessible without the user having to give consent to services and their cookies. Therefore, it is recommended to avoid processing personal data and setting cookies on these pages and not to display a cookie banner, but add those links to the footer of the cookie banner. Content blockers block loading of scripts etc. even on pages where the cookie banner is not displayed.")
                    }),
                    /*#__PURE__*/ _jsx(FormSettingsGeneralNavTable, {})
                ]
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                noStyle: true,
                shouldUpdate: (prevValues, nextValues)=>prevValues.isBannerLessConsent !== nextValues.isBannerLessConsent,
                children: (param)=>{
                    let { getFieldValue } = param;
                    const isBannerLessConsent = getFieldValue("isBannerLessConsent");
                    return /*#__PURE__*/ _jsx(Form.Item, {
                        label: __("Hide cookie banner on specific pages"),
                        children: /*#__PURE__*/ _jsx(Tooltip, {
                            title: isBannerLessConsent ? _i(__("The cookie banner is hidden on all subpages due to the active option {{a}}Consent > Banner-less consent{{/a}}."), {
                                a: /*#__PURE__*/ _jsx("a", {
                                    href: "#/settings/consent",
                                    style: {
                                        fontStyle: "italic"
                                    }
                                })
                            }) : undefined,
                            children: /*#__PURE__*/ _jsxs(Space.Compact, {
                                className: "rcb-antd-select-addon",
                                children: [
                                    /*#__PURE__*/ _jsx(Input, {
                                        addonBefore: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                __("Hide on additional pages"),
                                                "\xa0",
                                                /*#__PURE__*/ _jsx(UpsellTag, {
                                                    title: __("Want to hide cookie banner on more pages?"),
                                                    testDrive: true,
                                                    feature: "hide-page-ids",
                                                    description: __("Cookie banners are legally necessary, but distract your users from the essentials. For example, on landing pages for advertising campaigns, they lead to lower conversion rates. On these pages, it can be useful to avoid processing personal data and setting cookies to keep the conversion rate high. You can hide the cookie banner on these pages.")
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "hidePageIds",
                                        noStyle: true,
                                        children: /*#__PURE__*/ _jsx(RenderPageSelector, {
                                            id: "hidePageIds",
                                            disabled: !isPro || isBannerLessConsent,
                                            multiple: true
                                        })
                                    })
                                ]
                            })
                        })
                    });
                }
            }),
            /*#__PURE__*/ _jsxs(Form.Item, {
                label: __("Load services after consent via"),
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        noStyle: true,
                        shouldUpdate: (prevValues, nextValues)=>prevValues.isGcm !== nextValues.isGcm,
                        children: (param)=>{
                            let { getFieldValue } = param;
                            return /*#__PURE__*/ _jsx(Form.Item, {
                                name: "setCookiesViaManager",
                                noStyle: true,
                                children: /*#__PURE__*/ _jsx(Radio.Group, {
                                    children: /*#__PURE__*/ _jsxs(Space, {
                                        direction: "vertical",
                                        children: [
                                            /*#__PURE__*/ _jsx(Radio, {
                                                disabled: !isPro,
                                                value: "none",
                                                children: __("HTML/JavaScript Snippet")
                                            }),
                                            /*#__PURE__*/ _jsx(Tooltip, {
                                                title: isPro && !getFieldValue("isGcm") && _i(__("In order to be able to transfer consents to Google Tag Manager via Google Consent Mode, please activate this standard first under {{a}}Settings > Google Consent Mode{{/a}}."), {
                                                    a: /*#__PURE__*/ _jsx("a", {
                                                        href: "#/settings/gcm"
                                                    })
                                                }),
                                                children: /*#__PURE__*/ _jsxs(Radio, {
                                                    disabled: !isPro || !getFieldValue("isGcm"),
                                                    value: "googleTagManagerWithGcm",
                                                    children: [
                                                        __("Google Tag Manager with Google Consent Mode"),
                                                        "\xa0",
                                                        /*#__PURE__*/ _jsx(LearnMoreTag, {
                                                            url: __("https://devowl.io/knowledge-base/real-cookie-banner-with-google-tag-manager-google-consent-mode/")
                                                        })
                                                    ]
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs(Radio, {
                                                disabled: !isPro,
                                                value: "googleTagManager",
                                                children: [
                                                    __("%s Event", LABEL_GTM),
                                                    "\xa0",
                                                    /*#__PURE__*/ _jsx(LearnMoreTag, {
                                                        url: __("https://devowl.io/knowledge-base/real-cookie-banner-with-google-tag-manager-or-matomo-tag-manager/")
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs(Radio, {
                                                disabled: !isPro,
                                                value: "matomoTagManager",
                                                children: [
                                                    __("%s Event", LABEL_MTM),
                                                    "\xa0",
                                                    /*#__PURE__*/ _jsx(LearnMoreTag, {
                                                        url: __("https://devowl.io/knowledge-base/real-cookie-banner-with-google-tag-manager-or-matomo-tag-manager/")
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                })
                            });
                        }
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            margin: "5px 0px 0px"
                        },
                        children: /*#__PURE__*/ _jsx(UpsellTag, {
                            title: __("Want to use a Tag Manager legally?"),
                            testDrive: true,
                            feature: "set-cookies-via-manager",
                            assetName: __("pro-modal/set-cookies-via-manager.png"),
                            description: __("You can integrate services via Google Tag Manager or Matomo Tag Manager. At the same time, you can obtain and document consents via Real Cookie Banner in accordance with data protection regulations.")
                        })
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "description",
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: __("You only need to change this option if you use Google Tag Manager or Matomo Tag Manager.")
                            }),
                            "\xa0",
                            _i(__("If you opt-in to or opt-out from services, you will normally execute JavaScript code to ensure that scripts are enabled/disabled and cookies are set/removed on the visitor's client. If you use Google Tag Manager or Matomo Tag Manager, you can map this behavior using tags. However, even if they are executed via the tag manager, the tags may only be fired if consent has been given for the respective service. Real Cookie Banner can automatically transmit the consent to the respective Tag Manager via events or Google Consent Mode (recommended)."), {
                                strong: /*#__PURE__*/ _jsx("strong", {})
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export { useCreateCookiePolicyPageButton, FormSettingsGeneralTabContent, FormSettingsGeneralCreateCookiePolicyPageButton };
